import { useSnackbar } from 'notistack';
import { useState } from 'react';
// next
import { useRouter } from 'next/router';
// @mui
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Link,
  Button,
  Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import DIOText from '../../components/DIOText';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import {
  Before3WayCheckPrepDialog,
  PrepGuide3Way,
} from './meeting';
// utils
import { fKrDateDayKST, fKrTimeKST } from '../../utils/formatTime';

// ---------

export default function MeetingCard({ jobPost, candidate }) {
  const { status, pre_meeting_time: meetingTime, pre_meeting_link: meetingLink, check_list: checkList=null, prep_list: prepList=null, pre_meeting_weekend, pre_meeting_holiday } = candidate;
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const [openBefore3WayCheckPrepDialog, setOpenBefore3WayCheckPrepDialog] = useState((status === "waiting-3way-meeting") && !!checkList && !!prepList);
  const smUp = useResponsive('up', 'sm');
  const { remind: fromRemind } = router.query;
  const isHolidayOrWeekend = pre_meeting_weekend || pre_meeting_holiday;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(meetingLink);
      enqueueSnackbar('Zoom 링크를 복사하였습니다', { variant: 'info' });
    } catch (err) {
      console.error('failed to copy: ', err);
    }
  }

  return (
    <>
    <Card
      sx={{
        mb: 3,
        border: '1px solid'
     }}
    >
      <CardHeader
        title="사전 미팅 안내"
        titleTypographyProps={{ variant: "h5", gutterBottom: true }}
        subheader={"안내해드린 시간에 아래 Zoom 링크로 미팅에 참여해주세요."}
        subheaderTypographyProps={{ variant: smUp ? "body1" : "body2", color: "primary" }}
      />
      <CardContent sx={{ pt: isHolidayOrWeekend ? 1 : 2 }}>
      <Stack spacing={2} sx={{ mb: { xs: 5, sm: 1.5 } }}>
        {isHolidayOrWeekend && (
          <Box sx={{
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
            borderRadius: 1,
            border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Iconify icon="eva:alert-triangle-outline" sx={{ color: 'warning.main', width: 20, height: 20, flexShrink: 0, mt: 0.25 }} />
              <Typography variant="body2" color="warning.darker">
                한국 기준 주말 및 공휴일 미팅은 운영팀 미운영 시간으로, 상대 측 노쇼나 미팅 링크오류 발생 시 즉각적인 대응이 어려울 수 있습니다.<br/>이 경우 DIO 카카오톡 채널로 문의를 남겨주시면, 운영 시간에 확인 후 미팅 일정 재조율을 도와드리겠습니다.
              </Typography>
            </Stack>
          </Box>
        )}
        {/* date */}
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="subtitle1">
            {fKrDateDayKST(meetingTime)}
          </Typography>
        </Stack>
        {/* time */}
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/icon-clock-mono.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="subtitle1">
            {fKrTimeKST(meetingTime)}
          </Typography>
        </Stack>

        {/* 참석자 안내 */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <SvgIconStyle src={`/icons/icon-user-two-mono.svg`} sx={{ color: "secondary.main" }} />
          <Stack>
            <Typography variant="h6">
              참석자 2명 + <DIOText /> 레코딩 봇
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
              {jobPost?.company?.name} 담당자, {candidate?.crew_nickname} 크루
            </Typography>
          </Stack>
        </Stack>

        {/* zoom link */}
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-link-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="subtitle1">
              Zoom 링크
            </Typography>
            {meetingLink && (
              <Button
                size="small"
                variant="outlined"
                sx={{ borderColor: (theme) => theme.palette.grey[500_32] }}
                onClick={copyLink}
                startIcon={<SvgIconStyle src={`/icons/icon-share-mono.svg`} sx={{ width: 18, height: 18, color: "#8B95A1"}} />}
              >
                복사하기
              </Button>
            )}
          </Stack>
          <Stack sx={{ pl: 4, alignItems: "flex-start" }} spacing={0.5}>
            {meetingLink ? (
              <Link variant="subtitle1" underline="always" sx={{ wordBreak: "break-all", textDecorationColor: (theme) => theme.palette.primary.main }} color="text.primary" href={meetingLink} target="_blank" rel="noopener">
                {meetingLink}
              </Link>
            ) : (
              <Box sx={{ 
                p: 2, 
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderRadius: 1,
                border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
              }}>
                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                  ⚠️ Zoom 링크가 아직 생성되지 않았습니다.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  관리자에게 문의해주세요. (support@dio.so)
                </Typography>
              </Box>
            )}
          </Stack>
          <Stack sx={{ pl: 4, alignItems: "flex-start" }} spacing={0.5}>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }} color="text.secondary">
              Zoom 사전 미팅은 미팅 위반 행위(크루의 개인정보를 묻는 행위, 욕설 등) 체크를 위해 DIO에서 레코딩하며, 7일간 저장 후 파기됩니다.
            </Typography>
          </Stack>
        </Stack>

        {fromRemind && (
          <Box
            sx={{
              py: 3,
              px: { xs: 3, sm: 4 },
              background: (theme) => alpha(theme.palette.secondary.main, 0.08),
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <Iconify icon={'eva:alert-circle-fill'} sx={{ color: "secondary.main" }} width={20} height={20} />
              <Typography variant="subtitle1" sx={{ color: "secondary.main" }}>
                미팅이 끝나면,
              </Typography>
            </Stack>
            <Typography variant="body1">
              <b>크루가 먼저 계약 조건(금액, 업무 시간 등)을 제안</b>하게 됩니다. <br />
              크루가 제안한 계약 조건을 보고 수락, 거절, 수정 요청을 할 수 있습니다.
            </Typography>
          </Box>
        )}

      </Stack>
      </CardContent>
    </Card>
    {prepList && checkList && (
      <PrepGuide3Way prepList={prepList} checkList={checkList} />
    )}
    <Before3WayCheckPrepDialog
      isOpen={openBefore3WayCheckPrepDialog}
      closeDialog={() => setOpenBefore3WayCheckPrepDialog(false)}
      checkList={checkList}
      prepList={prepList}
    />
    </>
  )
}

